import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  query,
  where,
  getDocs,
  doc,
  getDoc,
  collectionGroup,
  collection,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import type { Agency, AgencyMember, Business } from '../types';

interface AgencyContextType {
  agencies: Agency[];
  currentAgency: Agency | null;
  currentMembership: AgencyMember | null;
  businesses: Business[]; // All businesses for current agency
  loading: boolean;
  error: string | null;
  setCurrentAgency: (agency: Agency | null) => void;
  refreshAgencies: () => Promise<void>;
  refreshBusinesses: () => Promise<void>;
}

const AgencyContext = createContext<AgencyContextType | undefined>(undefined);

export function AgencyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [currentAgency, setCurrentAgency] = useState<Agency | null>(null);
  const [currentMembership, setCurrentMembership] = useState<AgencyMember | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshAgencies = async () => {
    if (!user) {
      setAgencies([]);
      setCurrentAgency(null);
      setCurrentMembership(null);
      setBusinesses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Find all agency memberships for this user
      const membershipsQuery = query(
        collectionGroup(db, 'members'),
        where('userId', '==', user.uid)
      );
      const membershipsSnapshot = await getDocs(membershipsQuery);

      const agencyList: Agency[] = [];
      for (const memberDoc of membershipsSnapshot.docs) {
        // Check if this is an agency membership (parent collection is 'agencies')
        const parentRef = memberDoc.ref.parent.parent;
        if (!parentRef) continue;

        // Get the grandparent to check if it's agencies collection
        const collectionId = parentRef.parent.id;
        if (collectionId !== 'agencies') continue;

        const agencyId = parentRef.id;
        const agencyDoc = await getDoc(doc(db, 'agencies', agencyId));
        if (agencyDoc.exists()) {
          agencyList.push({
            id: agencyDoc.id,
            ...agencyDoc.data(),
          } as Agency);
        }
      }

      setAgencies(agencyList);

      // Set first agency as current if none selected
      if (agencyList.length > 0 && !currentAgency) {
        setCurrentAgency(agencyList[0]);
      }
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setError('Failed to load agencies');
    } finally {
      setLoading(false);
    }
  };

  const refreshBusinesses = async () => {
    if (!currentAgency) {
      setBusinesses([]);
      return;
    }

    try {
      // Get all businesses for the current agency
      const businessesQuery = query(
        collection(db, 'businesses'),
        where('agencyId', '==', currentAgency.id)
      );
      const businessesSnapshot = await getDocs(businessesQuery);

      const businessList: Business[] = [];
      for (const businessDoc of businessesSnapshot.docs) {
        businessList.push({
          id: businessDoc.id,
          ...businessDoc.data(),
        } as Business);
      }

      setBusinesses(businessList);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    }
  };

  useEffect(() => {
    refreshAgencies();
  }, [user]);

  // Update membership when current agency changes
  useEffect(() => {
    const fetchMembership = async () => {
      if (!user || !currentAgency) {
        setCurrentMembership(null);
        return;
      }

      try {
        const memberDoc = await getDoc(
          doc(db, 'agencies', currentAgency.id, 'members', user.uid)
        );
        if (memberDoc.exists()) {
          setCurrentMembership(memberDoc.data() as AgencyMember);
        } else {
          setCurrentMembership(null);
        }
      } catch (err) {
        console.error('Error fetching membership:', err);
        setCurrentMembership(null);
      }
    };

    fetchMembership();
    refreshBusinesses();
  }, [user, currentAgency]);

  const value = {
    agencies,
    currentAgency,
    currentMembership,
    businesses,
    loading,
    error,
    setCurrentAgency,
    refreshAgencies,
    refreshBusinesses,
  };

  return (
    <AgencyContext.Provider value={value}>{children}</AgencyContext.Provider>
  );
}

export function useAgency() {
  const context = useContext(AgencyContext);
  if (context === undefined) {
    throw new Error('useAgency must be used within an AgencyProvider');
  }
  return context;
}
